import { useState } from "react";
import { TrophyIcon } from "@heroicons/react/24/solid";

import Spinner from "./Spinner";
import { Modal, ModalHeader, ModalBody } from "./Modal";
import RatingDifferential from "./RatingDifferential";
import { Button } from "./ui/Button";
import { useHaterRankings2 } from "../hooks/useHaterRankings";

interface HaterRankingsProps {
  onBackToProfile?: () => void;
  isPublic?: boolean;
}

const HaterRankings2 = ({
  onBackToProfile,
  isPublic = false,
}: HaterRankingsProps) => {
  const { rankings, loading, error, refetch } = useHaterRankings2();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleClose = () => {
    setIsOpen(false);
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="text-center">
        <div className="text-letterboxd-error mb-4" role="alert">
          {error}
        </div>
        <Button onClick={refetch}>Try Again</Button>
      </div>
    );
  }

  // A component declared here would be a new type every render, remounting
  // the subtree and destroying the dialog trigger's focus-return target.
  const rankingsContent = (
    <div>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalHeader onClose={handleClose}>
          How ratings are calculated
        </ModalHeader>
        <ModalBody>
          <aside className="calculation-explanation">
            <p>
              Rating calculation: (sum(user.film_rating -
              letterboxd.film_rating) / total_films_rated) x 100
            </p>
            {/* TODO: PUT EXPLANATION IN TOOLTIP, GENERALIZE TOOLTIP */}
            <ol>
              <li>
                For each user's rated movie, get the difference between the
                user's rating of a movie and the average Letterboxd rating (i.e.
                if user rated a movie 3.5 and the average letterboxd score is 4,
                the differntial is -0.5).
              </li>
              <li>Sum all of the differences of each movie</li>
              <li>Divide the sum by the number of films the user rated</li>
              <li>Multiply that result by 100</li>
              <li>
                (sum(user.film_rating - letterboxd.film_rating) /
                total_films_rated) x 100
              </li>
            </ol>
          </aside>
        </ModalBody>
      </Modal>
      <h1 className="text-3xl font-bold text-letterboxd-text-primary mb-2">
        Hater Rankings
      </h1>
      <h3 className="subheading">
        Based on differential from average Letterboxd ratings per 100 movies
      </h3>
      <button
        type="button"
        className="text-xs mb-4 underline text-letterboxd-text-secondary hover:text-letterboxd-text-primary"
        onClick={() => setIsOpen(true)}
      >
        How is this calculated?
      </button>
      <div>
        {rankings.length === 0 ? (
          <div className="px-6 py-8 text-center text-letterboxd-text-secondary">
            No user ratings found. Users need to have their ratings scraped
            first.
          </div>
        ) : (
          <div>
            <table className="w-full">
              <thead>
                <tr className="table-heading-row">
                  <th scope="col">Rank</th>
                  <th scope="col">User</th>
                  <th scope="col">Total films rated</th>
                  <th scope="col" className="flex items-center">
                    Differential (per 100 rated movies)
                  </th>
                </tr>
              </thead>
              <tbody>
                {rankings.map((ranking, index) => (
                  <tr key={ranking.username}>
                    <td className="px-6 py-1 whitespace-nowrap text-lg text-letterboxd-text-primary font-bold">
                      {index + 1}
                    </td>
                    <td className="px-6 py-1 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {index === 0 && (
                          <TrophyIcon className="h-5 w-5 text-letterboxd-pro" />
                        )}
                        <span
                          className={`font-medium text-xl ${
                            index === 0
                              ? "text-letterboxd-pro"
                              : "text-letterboxd-text-primary"
                          }`}
                        >
                          <a href={`/user/${ranking.username}`}>
                            {ranking.displayName || ranking.username}
                          </a>
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-1 whitespace-nowrap text-sm text-letterboxd-text-secondary">
                      {ranking.filmsRated}
                    </td>
                    <td className="px-6 py-1 whitespace-nowrap text-sm text-letterboxd-text-primary">
                      <RatingDifferential
                        differential={ranking.adjustedDifferential}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rankings.length > 0 && (
        <div className="text-center text-sm text-letterboxd-text-secondary">
          <p>
            Showing {rankings.length} user{rankings.length !== 1 ? "s" : ""}{" "}
            with ratings data
          </p>
        </div>
      )}
    </div>
  );

  return rankingsContent;
};

export default HaterRankings2;
